import React, { useContext } from 'react';
import { Grid, IconButton, Stack, Typography } from '@mui/material';
import { MetWidgetPaper } from 'components/shared/common';
import { DocumentItem } from 'models/document';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useAppDispatch } from 'hooks';
import { openNotificationModal } from 'services/notificationModalService/notificationModalSlice';
import { deleteDocument } from 'services/widgetService/DocumentService';
import { WidgetDrawerContext } from '../WidgetDrawerContext';
import { WidgetType, Widget } from 'models/widget';
import Edit from '@mui/icons-material/Edit';
import { DocumentsContext } from './DocumentsContext';
import { DraggableProvided } from '@hello-pangea/dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LinkIcon from '@mui/icons-material/Link';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

const DocumentFile = ({
    documentItem,
    draggableProvided,
}: {
    documentItem: DocumentItem;
    draggableProvided: DraggableProvided;
}) => {
    const dispatch = useAppDispatch();
    const { handleAddFileDrawerOpen, handleChangeDocumentToEdit, loadDocuments } = useContext(DocumentsContext);
    const { widgets } = useContext(WidgetDrawerContext);
    const documentWidget = widgets.find((widget: Widget) => widget.widget_type_id === WidgetType.Document);

    const handleEditDocument = () => {
        handleChangeDocumentToEdit(documentItem);
        handleAddFileDrawerOpen(true);
    };

    const handleDeleteDocument = async () => {
        if (!documentWidget) {
            return;
        }
        await deleteDocument(documentWidget.id, documentItem.id);
        await loadDocuments();
    };

    return (
        <Grid item xs={12} container alignItems="flex-start" justifyContent={'flex-start'} spacing={2} mb={2}>
            <MetWidgetPaper elevation={1} sx={{ width: '100%' }}>
                <Grid container direction="row" alignItems={'center'} justifyContent="flex-start">
                    <Grid item sx={{ alignItems: 'center', justifyContent: 'center' }}>
                        <IconButton
                            sx={{ margin: '0 0.5em 0 0', padding: 0 }}
                            color="inherit"
                            aria-label="drag-indicator"
                            {...draggableProvided.dragHandleProps}
                        >
                            <DragIndicatorIcon />
                        </IconButton>
                    </Grid>
                    <Grid item xs>
                        <Stack spacing={2} direction="row" alignItems="center">
                            {documentItem.is_uploaded ? <InsertDriveFileIcon /> : <LinkIcon />}
                            <Typography
                                sx={{
                                    whiteSpace: 'normal',
                                    width: '90%',
                                    wordWrap: 'break-word',
                                }}
                            >
                                {documentItem.title}
                            </Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs container justifyContent={'flex-end'}>
                        <IconButton
                            onClick={() => handleEditDocument()}
                            sx={{ padding: 0, mr: 1 }}
                            color="inherit"
                            aria-label="Edit Folder"
                        >
                            <Edit />
                        </IconButton>
                        <IconButton
                            onClick={() =>
                                dispatch(
                                    openNotificationModal({
                                        open: true,
                                        data: {
                                            header: 'Remove File',
                                            subText: [
                                                {
                                                    text: 'You will be removing this file from the engagement.',
                                                },
                                                { text: 'Do you want to remove this file?' },
                                            ],
                                            handleConfirm: () => {
                                                handleDeleteDocument();
                                            },
                                        },
                                        type: 'confirm',
                                    }),
                                )
                            }
                            sx={{ padding: 0, margin: 0 }}
                            color="inherit"
                            aria-label="Remove File"
                        >
                            <HighlightOffIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            </MetWidgetPaper>
        </Grid>
    );
};

export default DocumentFile;
